import { ChangeEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { DashboardDocuments, DocumentsInfoContainer } from './businessDocuments.styles';
import { ModalDocumentsSucess } from 'src/components/shared/modals/documentsUploaded/documentsUploaded.styles';
import { InputUpload } from 'src/utilities/UILibrary/Forms/InputUpload/InputUpload';
import { Button } from 'src/utilities/UILibrary/UI/Button/Button';
import { environment, url } from 'src/environments';
import Modal from 'src/utilities/UILibrary/Feedback/Modal/Modal';
import { RootState } from 'src/store/store';
import { Checkbox } from 'src/utilities/UILibrary/Forms/Checkbox/Checkbox';
import { InputField } from 'src/utilities/UILibrary/Forms/InputField/InputField';
import { UploadButton } from 'src/utilities/UILibrary/Forms/UploadButton/UploadButton';

type UploadedFiles = {
  constitutiveAct: File | null;
  actInscription: File | null;
  addressFile: File | null;
  legalRep: File | null;
  idFile: File[];
  statement: File | null;
};

export default function BusinessDocuments() {
  const user = useSelector((state: RootState) => state.user.currentUser);
  const [uploadStatus, setUploadStatus] = useState('pending');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFiles>({
    constitutiveAct: null,
    actInscription: null,
    addressFile: null,
    idFile: [],
    legalRep: null,
    statement: null
  });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [legalRepresentativeUploaded, setLegalRepresentativeUploaded] = useState(false);
  const [legallyBoundUploaded, setLegallyBoundUploaded] = useState(false);
  const [legallyBoundName, setLegallyBoundName] = useState<string>();
  const [localLoading, setLocalLoading] = useState(false);
  const [internalErrors, setInternalErrors] = useState({
    legalRepresentativeName: false
  });

  //FIle to base64
  const fileToBase64 = async (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        }
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    try {
      const base64String = await fileToBase64(file);
      return base64String;
    } catch (error) {
      console.error('Error al convertir el archivo a Base64:', error);
    }
  };

  const sendToServer = async () => {
    setUploadStatus('uploading');

    const fileKeys = Object.keys(uploadedFiles);

    const input = {
      id: user.id,
      files: [] as { base64File: unknown; folder: string; fileName: string }[]
    };

    for (const key of fileKeys) {
      if (key === 'idFile') {
        let idFileCounter = 0;
        for (const file of uploadedFiles[key]) {
          const base64File = await handleFile(file);
          let fileName;
          if (idFileCounter === 0) {
            fileName = `${key}_legally_bound.pdf`;
          } else {
            fileName = `${key}_${idFileCounter}.pdf`;
          }
          input.files.push({
            base64File,
            folder: 'businessDocuments',
            fileName: fileName
          });
          idFileCounter++;
        }
      } else {
        const base64File = await handleFile(
          uploadedFiles[key as keyof Omit<UploadedFiles, 'idFile'>]
        );
        input.files.push({
          base64File,
          folder: 'businessDocuments',
          fileName: `${key}.pdf`
        });
      }
    }

    const legallyBoundBody = {
      type: 'update_applicant_obligadoSolidario',
      input: {
        id: user.id,
        companyInformation: {
          obligadoSolidario: legallyBoundName
        }
      }
    };

    try {
      setLocalLoading(true);
      const upload = await axios.post(
        `${url}/${environment}/admin/documents/upload-multiple`,
        input
      );
      await axios.post(`${url}/${environment}/admin/dynamo/update`, legallyBoundBody);

      const responseBody = JSON.parse(upload.data.body);
      if (responseBody.result) {
        setUploadStatus('completed');
      } else {
        setUploadStatus('failed');
      }
      setLocalLoading(false);
    } catch (error) {
      console.error(error);
      setUploadStatus('failed');
      setLocalLoading(false);
    }
  };

  const handleUploadLegalRepresentatives = (e: ChangeEvent<HTMLInputElement>) => {
    setUploadedFiles({
      ...uploadedFiles,
      idFile: e.target.files
        ? [...uploadedFiles.idFile, ...Array.from(e.target.files)]
        : uploadedFiles.idFile
    });
    setShowUploadModal(false);

    if (legallyBoundName !== undefined) {
      setLegallyBoundUploaded(true);
    }
  };

  return (
    <DocumentsInfoContainer>
      <Modal
        onClose={() => setShowUploadModal(false)}
        isOpen={showUploadModal}
        onOverlayClose={false}
      >
        <Modal.Header>
          <h2>Documentos de la empresa</h2>
        </Modal.Header>
        <Modal.Body>
          <p>
            En esta sección podrás cargar los documentos necesarios para que puedas crear tus
            campañas. Por favor introduce el nombre del obligado solidario, asegurate de que el
            nombre sea el correcto y despues carga el archivo.
          </p>
          {legallyBoundUploaded ? null : (
            <InputField
              label="Nombre del obligado solidario"
              onChangeInternalValue={(value) => {
                const formattedName = value
                  .toString()
                  .toLowerCase()
                  .split(' ')
                  .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
                  .join(' ');
                setLegallyBoundName(formattedName);
              }}
              regex={/^[a-zA-ZÀ-ÿ]+\s([a-zA-ZÀ-ÿ]*\s)?[a-zA-ZÀ-ÿ]+\s[a-zA-ZÀ-ÿ]+$/}
              errorRegexMessage="El nombre debe ser el correcto y no debe contener números, espacios, ni caracteres especiales"
              onChangeError={(error) =>
                setInternalErrors({ ...internalErrors, legalRepresentativeName: error })
              }
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <UploadButton
            id="idFile"
            size="small"
            accept=".pdf"
            onChange={(e) => handleUploadLegalRepresentatives(e)}
            disabled={
              legallyBoundName && legallyBoundName.length > 1
                ? internalErrors.legalRepresentativeName
                : true
            }
          >
            Cargar archivo
          </UploadButton>
        </Modal.Footer>
      </Modal>

      <ModalDocumentsSucess />
      <h2>
        En esta sección podrás cargar los documentos necesarios para que puedas crear tus campañas
      </h2>
      <h3>Fecha de última carga de archivos:</h3>
      <DashboardDocuments>
        <div>
          <p>Acta Constitutiva </p>
          <InputUpload
            label="Acta constitutiva"
            id="constitutiveAct"
            size="small"
            accept=".pdf"
            onChange={(e) => {
              setUploadedFiles({
                ...uploadedFiles,
                constitutiveAct: e.target.files?.[0] || null
              });
            }}
          >
            Cargar archivo
          </InputUpload>
        </div>
        <div>
          <p>Inscripción del Acta Constitutiva</p>
          <InputUpload
            label="Inscripción del Acta Constitutiva"
            id="actInscription"
            size="small"
            accept=".pdf"
            onChange={(e) => {
              setUploadedFiles({
                ...uploadedFiles,
                actInscription: e.target.files?.[0] || null
              });
            }}
          >
            Cargar archivo
          </InputUpload>
        </div>
        <div>
          <p>Antigüedad no mayor a 3 meses</p>
          <InputUpload
            label="Comprobante de domicilio"
            id="addressFile"
            size="small"
            accept=".pdf"
            onChange={(e) => {
              setUploadedFiles({
                ...uploadedFiles,
                addressFile: e.target.files?.[0] || null
              });
            }}
          >
            Cargar archivo
          </InputUpload>
        </div>
        <div>
          <p>
            Documento que contenga los poderes del representante(s) legales, cuando no estén
            contenidos en el Acta Constitutiva y la identificación personal de cada uno de los
            representantes.
          </p>
          <InputUpload
            label="Poderes del representante o representantes"
            id="legalRep"
            size="small"
            accept=".pdf"
            onChange={(e) => {
              setUploadedFiles({
                ...uploadedFiles,
                legalRep: e.target.files?.[0] || null
              });
            }}
          >
            Cargar archivo
          </InputUpload>
        </div>
        <div>
          <p>
            Identificación personal de cada uno de los representantes legales. En caso de ser
            extranjero proporciónanos tu pasaporte o algún documento válidos de identificación
            personal oficial emitida por autoridades federales de tu país de origen.
          </p>
          <div className="button-box">
            <p>Identificación personal</p>
            <Button
              isSlim
              isFullWidth
              size="small"
              onClick={() => setShowUploadModal(true)}
              disabled={legalRepresentativeUploaded}
            >
              Cargar archivo
            </Button>
          </div>
        </div>
        <div>
          <p>
            Declaración firmada por el representante legal que señale quiénes son sus Propietarios
            Reales
          </p>
          <InputUpload
            label="Declaración"
            id="statement"
            size="small"
            accept=".pdf"
            onChange={(e) => {
              setUploadedFiles({
                ...uploadedFiles,
                statement: e.target.files?.[0] || null
              });
            }}
          >
            Cargar archivo
          </InputUpload>
        </div>
      </DashboardDocuments>
      <div className="submit-button">
        <Checkbox
          label="Marca esta casilla si has subido todos los representantes legales en Identificación personal"
          onChange={(e) => setLegalRepresentativeUploaded(e.target.checked)}
          disabled={uploadedFiles.idFile.length === 0}
        />
        <Button
          onClick={() => {
            if (uploadStatus === 'completed') {
              setUploadedFiles({
                constitutiveAct: null,
                actInscription: null,
                addressFile: null,
                legalRep: null,
                idFile: [],
                statement: null
              });
              setUploadStatus('pending');
            } else {
              sendToServer();
            }
          }}
          isSlim
          isFullWidth
          disabled={
            uploadStatus === 'uploading' ||
            !Object.values(uploadedFiles).every((value) => value) ||
            !legalRepresentativeUploaded
          }
          isLoading={localLoading}
        >
          {uploadStatus === 'completed' ? 'Actualizar archivos' : 'Subir todos'}
        </Button>
      </div>
    </DocumentsInfoContainer>
  );
}
